import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getJobList, searchJobs, addJob } from '../api'

/**
 * Hook to fetch job list with pagination and sorting
 * @param {string} status - Job status (open, pending, submitted, archived)
 * @param {number} page - Current page number
 * @param {number} perPage - Items per page (from user.list_count)
 * @param {string} sortBy - Field to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {object} Query result
 */
export const useJobList = (status, page = 1, perPage = 100, sortBy = 'created_at', sortDirection = 'asc') => {
	// Use a placeholder key when status is null to avoid creating unnecessary cache entries
	const queryKey = status ? ['jobs', status, page, perPage, sortBy, sortDirection] : ['jobs-placeholder'];
	
	return useQuery({
		queryKey: queryKey,
		queryFn: () => getJobList(status, { 
			page, 
			per_page: perPage,
			sort_by: sortBy,
			sort_direction: sortDirection
		}),
		enabled: !!status,
		keepPreviousData: true, // Keep previous page data while fetching new page
		select: (data) => ({
			jobs: data.jobs || [],
			pagination: data.pagination || {}
		})
	})
}

/**
 * Hook to search for jobs
 * @returns {object} Mutation result with mutate function
 */
export const useSearchJobs = () => {
	return useMutation({
		mutationFn: (searchTerm) => searchJobs({ query: searchTerm }),
		onError: (error) => {
			console.error('Error searching jobs:', error)
		},
	})
}

/**
 * Hook to create a new job
 * @returns {object} Mutation result with mutate function
 */
export const useAddJob = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (jobData) => addJob(jobData),
		onSuccess: (newJob) => {
			// Invalidate and refetch job lists to include the new job
			queryClient.invalidateQueries({ queryKey: ['jobs'] })
		},
		onError: (error) => {
			console.error('Error adding job:', error)
			return { type: 'error', payload: error }
		},
	})
}

